import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, CalendarDays, CheckSquare, ChevronRight, ClipboardList, Monitor, Shirt, Video, Award, Bell } from 'lucide-react';
import { getHome } from '@/api/student';
import { QueryState } from '@/components/kit/ui';
import { unwrap } from '@/components/kit/data';
import type { TimetableRow } from '../shared';

interface Learning {
  lessons: { new: number; in_progress: number; todo: { id: number; topic: string; subject: string | null; state: string; min_reading_minutes: number | null; due_at: string | null }[] };
  live: { id: number; title: string; starts_at: string; state: string; teacher: string | null; count: number } | null;
  tests_open: number;
  last_result: { term: string; average: number | null; position: string | null } | null;
}
interface Home {
  student: { id: number; name: string; admission_no: string; status: string; photo_path: string | null };
  class: string | null; alumni?: boolean;
  today?: TimetableRow[]; uniform_today?: { name: string; description: string | null } | null;
  attendance_month?: { days: number; present: number; absent: number };
  assignments_due?: number; unread_notices?: number; results_held?: boolean; unread_notifications?: number;
  learning?: Learning;
}

const when = (s: string) => new Date(s).toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });

function Tile({ to, label, value, hint, icon: Icon, tone = 'text-slate-900' }: { to: string; label: string; value: ReactNode; hint?: string; icon: any; tone?: string }) {
  return (
    <Link to={to} className="bg-white rounded-2xl border border-slate-200 p-3.5 hover:border-slate-300 block">
      <div className="flex items-center justify-between"><p className="text-xs text-slate-500 truncate">{label}</p><Icon className="w-4 h-4 text-slate-400" /></div>
      <p className={`text-2xl font-extrabold mt-1 ${tone}`}>{value}</p>
      {hint && <p className="text-[11px] text-slate-500 truncate">{hint}</p>}
    </Link>
  );
}

function Panel({ title, to, link, children }: { title: string; to?: string; link?: string; children: ReactNode }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-200">
      <div className="flex items-center justify-between px-4 pt-3.5 pb-2"><h2 className="font-bold text-slate-900">{title}</h2>{to && <Link to={to} className="text-xs font-semibold text-[#0A2540] inline-flex items-center">{link ?? 'Open'}<ChevronRight className="w-3.5 h-3.5" /></Link>}</div>
      <div className="px-4 pb-4">{children}</div>
    </section>
  );
}

export function DashboardPage() {
  const q = useQuery({ queryKey: ['student-home'], queryFn: () => getHome().then((r) => unwrap<Home>(r)) });
  const d = q.data;
  const att = d?.attendance_month;
  const L = d?.learning;
  const pct = att && att.days ? Math.round((att.present / att.days) * 100) : null;

  return (
    <div className="space-y-4 max-w-6xl">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{d ? [d.class, d.student?.admission_no].filter(Boolean).join(' · ') : 'Student'}</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">{d?.student?.name ? `Hi, ${d.student.name.split(' ')[0]}` : 'Dashboard'}</h1>
      </div>
      <QueryState q={q}>
        {d && !d.class ? (
          <Panel title={d.alumni ? 'You have graduated' : 'No active class'}>
            <p className="text-sm text-slate-600">{d.alumni ? 'You can still view your results, download your transcript and request documents.' : 'You are not in an active class at the moment. Ask the school office for help.'}</p>
            <Link to="/student/results" className="inline-block mt-3 text-sm font-semibold text-[#0A2540]">Go to my results →</Link>
          </Panel>
        ) : d && (
          <>
            {d.results_held && <p className="text-sm rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3">Your results are being held by the school for now. Your parent or guardian can see why on their app.</p>}

            {L?.live && (
              <Link to="/student/live" className="flex items-center gap-3 rounded-2xl bg-[#0A2540] text-white p-4">
                <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${L.live.state === 'live' ? 'bg-red-500' : 'bg-white/15'}`}><Video className="w-5 h-5" /></span>
                <span className="flex-1 min-w-0">
                  <span className="block text-xs text-white/60 font-bold uppercase tracking-wider">{L.live.state === 'live' ? 'Live now — join' : L.live.state === 'open' ? 'Open to join' : 'Next live class'}</span>
                  <span className="block font-semibold truncate">{L.live.title}</span>
                  <span className="block text-xs text-white/70 truncate">{[L.live.teacher, when(L.live.starts_at)].filter(Boolean).join(' · ')}</span>
                </span>
                <ChevronRight className="w-5 h-5" />
              </Link>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Tile to="/student/learn" icon={BookOpen} label="Lessons to read" value={(L?.lessons.new ?? 0) + (L?.lessons.in_progress ?? 0)} hint={L ? `${L.lessons.in_progress} in progress` : undefined} tone={L?.lessons.new ? 'text-blue-700' : 'text-slate-900'} />
              <Tile to="/student/learn" icon={ClipboardList} label="Assignments due" value={d.assignments_due ?? 0} tone={d.assignments_due ? 'text-amber-600' : 'text-slate-900'} />
              <Tile to="/student/tests" icon={Monitor} label="Tests open" value={L?.tests_open ?? 0} tone={L?.tests_open ? 'text-red-600' : 'text-slate-900'} />
              <Tile to="/student/attendance" icon={CheckSquare} label="Attendance (month)" value={pct === null ? '—' : `${pct}%`} hint={att && att.days ? `${att.present} of ${att.days} days present` : 'Not marked yet'} tone={pct !== null && pct < 75 ? 'text-red-600' : 'text-green-700'} />
            </div>

            <div className="grid lg:grid-cols-[3fr_2fr] gap-4">
              <Panel title="Up next to read" to="/student/learn" link="All lessons">
                {!L?.lessons.todo.length ? <p className="text-sm text-slate-500">You are all caught up. 🎉</p> : (
                  <ul className="divide-y divide-slate-100">
                    {L.lessons.todo.map((l) => (
                      <li key={l.id}>
                        <Link to={`/student/learn/lessons/${l.id}`} className="py-2.5 flex items-center gap-3">
                          <span className={`w-2 h-2 rounded-full shrink-0 ${l.state === 'new' ? 'bg-blue-600' : 'bg-amber-500'}`} />
                          <span className="flex-1 min-w-0"><span className="block text-sm font-semibold text-slate-900 truncate">{l.topic}</span>
                            <span className="block text-xs text-slate-500 truncate">{[l.subject, l.min_reading_minutes ? `${l.min_reading_minutes} min read` : null, l.due_at ? `due ${new Date(l.due_at).toLocaleDateString()}` : null].filter(Boolean).join(' · ')}</span></span>
                          <span className="text-[11px] font-semibold text-slate-500">{l.state === 'new' ? 'New' : 'Continue'}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
              <Panel title="Today's classes" to="/student/timetable" link="Timetable">
                {!d.today?.length ? <p className="text-sm text-slate-500">No classes on your timetable today.</p> : (
                  <ul className="divide-y divide-slate-100 text-sm">
                    {d.today.map((e) => (
                      <li key={e.id} className="py-2 flex gap-3">
                        <span className="font-mono text-xs text-slate-500 w-24 shrink-0 pt-0.5">{e.starts_at}–{e.ends_at}</span>
                        <span className="min-w-0 truncate"><b>{e.subject ?? (e.kind === 'break' ? 'Break' : e.kind ?? 'Free period')}</b>{e.teacher ? <span className="text-slate-500"> · {e.teacher}</span> : null}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <Link to="/student/results" className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3"><Award className="w-5 h-5 text-[#0A2540]" />
                <span className="flex-1 min-w-0"><span className="block text-xs text-slate-500">Last result</span><span className="block font-semibold truncate">{L?.last_result ? `${L.last_result.term}${L.last_result.average != null ? ` · ${L.last_result.average}%` : ''}${L.last_result.position ? ` · position ${L.last_result.position}` : ''}` : 'No result yet'}</span></span></Link>
              <Link to="/student/uniform" className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3"><Shirt className="w-5 h-5 text-[#0A2540]" />
                <span className="flex-1 min-w-0"><span className="block text-xs text-slate-500">Uniform today</span><span className="block font-semibold truncate">{d.uniform_today?.name ?? 'Not set'}</span></span></Link>
              <Link to="/student/calendar" className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3">{d.unread_notices ? <Bell className="w-5 h-5 text-red-600" /> : <CalendarDays className="w-5 h-5 text-[#0A2540]" />}
                <span className="flex-1 min-w-0"><span className="block text-xs text-slate-500">Notices</span><span className="block font-semibold truncate">{d.unread_notices ? `${d.unread_notices} unread` : 'All read'}</span></span></Link>
            </div>
          </>
        )}
      </QueryState>
    </div>
  );
}
